import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
const utc = (iso) => new Date(/Z|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
const t = (iso) => (iso ? utc(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const Sig = ({ src, testId }) => (src ? <img src={src} alt="Firma" className="h-12 w-28 rounded border border-slate-200 bg-white object-contain" data-testid={testId} /> : <span className="text-xs text-amber-700">Firma mancante</span>);

export const download = async (url, params, fallback) => {
  try {
    const { data: blob, headers } = await api.get(url, { params, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || fallback;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(errMsg(e)); }
};

const DailyList = ({ rows, onReturn }) => (
  <div className="space-y-2" data-testid="phone-register-daily">
    {rows.map((a) => (
      <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3" data-testid={`phone-register-row-${a.id}`}>
        <div className="min-w-[10rem] flex-1">
          <p className="font-semibold">{a.assignee}</p>
          <p className="text-xs text-slate-500">{a.brand} {a.model} · IMEI {a.imei}{a.phone_number ? ` · ${a.phone_number}` : ""} · {a.station_name}</p>
        </div>
        <div className="text-xs"><p className="mb-1 text-slate-500">Ritiro {t(a.pickup_at)}</p><Sig src={a.pickup_signature} testId={`phone-register-pickup-sig-${a.id}`} /></div>
        <div className="text-xs">
          {a.returned_at
            ? <><p className="mb-1 text-slate-500">Riconsegna {t(a.returned_at)}</p><Sig src={a.return_signature} testId={`phone-register-return-sig-${a.id}`} /></>
            : <Button size="sm" variant="outline" onClick={() => onReturn(a)} data-testid={`phone-register-return-${a.id}`}><Undo2 className="mr-1 h-3.5 w-3.5" /> Riconsegna</Button>}
        </div>
      </div>
    ))}
    {rows.length === 0 && <p className="py-8 text-center text-sm text-slate-500" data-testid="phone-register-empty">Nessuna consegna giornaliera in questa data.</p>}
  </div>
);

const FixedList = ({ rows }) => (
  <div className="space-y-2" data-testid="phone-register-fixed">
    {rows.map((p) => (
      <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3" data-testid={`phone-fixed-row-${p.id}`}>
        <div className="min-w-[10rem] flex-1">
          <p className="font-semibold">{p.fixed_assignee}</p>
          <p className="text-xs text-slate-500">{p.brand} {p.model} · IMEI {p.imei}{p.phone_number ? ` · ${p.phone_number}` : ""} · {p.station_name}</p>
          {p.fixed_since && <p className="text-xs text-slate-400">Dal {utc(p.fixed_since).toLocaleDateString("it-IT")}</p>}
        </div>
        <Sig src={p.fixed_signature} testId={`phone-fixed-sig-${p.id}`} />
      </div>
    ))}
    {rows.length === 0 && <p className="py-8 text-center text-sm text-slate-500">Nessun assegnatario fisso.</p>}
  </div>
);

export const PhoneRegister = ({ open, stationId, refreshKey, onClose, onReturn }) => {
  const [tab, setTab] = useState("daily");
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    if (!open || !date) return;
    const p = { station_id: stationId };
    (tab === "daily" ? api.get("/phones/register", { params: { ...p, date } }) : api.get("/phones/fixed-register", { params: p }))
      .then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open, tab, date, stationId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="phone-register-dialog">
        <DialogHeader>
          <DialogTitle>Schedario telefoni</DialogTitle>
          <DialogDescription>Assegnatari fissi con firma e registro giornaliero di ritiri e riconsegne.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            {[["daily", "Giornalieri"], ["fixed", "Fissi"]].map(([k, l]) => (
              <button key={k} onClick={() => { if (k !== tab) { setRows([]); setTab(k); } }} data-testid={`phone-register-tab-${k}`}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div>
          {tab === "daily" && <>
            <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="phone-register-date" />
            <Button variant="outline" size="sm" onClick={() => download("/phones/register/export", { station_id: stationId, date }, `schedario_${date}.xlsx`)} data-testid="phone-register-export"><FileSpreadsheet className="mr-1 h-4 w-4" /> Excel</Button>
          </>}
        </div>
        {tab === "daily" ? <DailyList rows={rows} onReturn={onReturn} /> : <FixedList rows={rows} />}
      </DialogContent>
    </Dialog>
  );
};
